/**
 * RSI_DIVERGENCE — Bullish / bearish divergence overlay for the RSI sub-pane
 *
 * Stacked companion to the native 'RSI' indicator (same pattern as
 * VOL_LABELS/CROSS_VOL stacked onto vol_pane).
 *
 * Computes a dedicated internal RSI — independent of whatever periods the
 * user has configured for the visible RSI1/2/3 lines — using klinecharts'
 * own rolling-window-sum formula (see the native 'RSI' calc in
 * klinecharts/dist/index.esm.js) so this stays internally consistent with
 * how RSI already behaves elsewhere in the app. Its period, along with the
 * pivot fractal's lookback width, are user-configurable via `calcParams`
 * (`[rsiPeriod, pivotLookback]`, default `[14, 2]`) — surfaced as "RSI
 * Period" / "Pivot Lookback" inputs under RSI's "Show Divergence" toggle in
 * IndicatorConfigModal.tsx.
 *
 * Pivot detection: an (2*pivotLookback + 1)-bar fractal (default 5-bar: a
 * 2-bar lookback + 2-bar lookahead) on price highs/lows. Regular divergence
 * only (no hidden-divergence variants):
 *   Bullish: new pivot low with a lower price low but a higher dedicated-RSI
 *            low than the immediately preceding pivot low.
 *   Bearish: new pivot high with a higher price high but a lower
 *            dedicated-RSI high than the immediately preceding pivot high.
 *
 * Draw: dashed connector line + small circle markers between the two
 * dedicated-RSI pivot points, on the RSI pane's own 0-100 y-axis.
 */

import { IndicatorSeries, type IndicatorTemplate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';

// ── Types ─────────────────────────────────────────────────────────────────

type KBar = { open: number; high: number; low: number; close: number };

type DivType = 'bullish' | 'bearish' | null;

export type RsiDivergenceResult = {
  rsi:           number | null;
  divType:       DivType;
  pairFromIndex: number | null;
};

type DrawParams = {
  ctx:          CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  xAxis:        { convertToPixel: (i: number) => number };
  yAxis:        { convertToPixel: (v: number) => number };
  indicator:    { result: RsiDivergenceResult[] };
};

// ── Defaults / constants ─────────────────────────────────────────────────

export const DEFAULT_RSI_PERIOD = 14;
export const DEFAULT_PIVOT_LOOKBACK = 2; // 2-bar lookback + 2-bar lookahead = 5-bar fractal
const DRAW_LOOKBACK_PAD = 60; // extend the scan window left of visibleRange so connectors whose left endpoint is off-screen still render

const BULLISH_COLOR = '#22C55E'; // green
const BEARISH_COLOR = '#F97316'; // orange

// ── Dedicated RSI — rolling-window-sum formula (matches native RSI) ───────

function computeRsi(bars: KBar[], period: number): (number | null)[] {
  const n = bars.length;
  const rsi: (number | null)[] = new Array(n).fill(null);
  const gains: number[] = new Array(n).fill(0);
  const losses: number[] = new Array(n).fill(0);

  let sumGain = 0;
  let sumLoss = 0;

  for (let i = 1; i < n; i++) {
    const diff = bars[i].close - bars[i - 1].close;
    gains[i]  = diff > 0 ? diff : 0;
    losses[i] = diff < 0 ? -diff : 0;

    sumGain += gains[i];
    sumLoss += losses[i];

    const dropIdx = i - period;
    if (dropIdx >= 1) {
      sumGain -= gains[dropIdx];
      sumLoss -= losses[dropIdx];
    }

    if (i >= period) {
      rsi[i] = sumLoss === 0 ? 100 : 100 - 100 / (1 + sumGain / sumLoss);
    }
  }

  return rsi;
}

// ── Pivot detection — (2*lookback + 1)-bar fractal ─────────────────────────

function isPivotLow(bars: KBar[], i: number, lookback: number): boolean {
  const lo = bars[i].low;
  let hasStrictGreater = false;
  for (let k = i - lookback; k <= i + lookback; k++) {
    if (k === i) continue;
    if (bars[k].low < lo) return false;
    if (bars[k].low > lo) hasStrictGreater = true;
  }
  return hasStrictGreater;
}

function isPivotHigh(bars: KBar[], i: number, lookback: number): boolean {
  const hi = bars[i].high;
  let hasStrictLower = false;
  for (let k = i - lookback; k <= i + lookback; k++) {
    if (k === i) continue;
    if (bars[k].high > hi) return false;
    if (bars[k].high < hi) hasStrictLower = true;
  }
  return hasStrictLower;
}

// ── Indicator template ────────────────────────────────────────────────────

const rsiDivergence: IndicatorTemplate<RsiDivergenceResult> = {
  name:       'RSI_DIVERGENCE',
  shortName:  'RSI Divergence',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,
  figures:    [], // all rendering is manual
  calcParams: [DEFAULT_RSI_PERIOD, DEFAULT_PIVOT_LOOKBACK],

  calc: (dataList, indicator) => {
    const bars = dataList as unknown as KBar[];
    const n = bars.length;
    const rawParams = (indicator as { calcParams?: number[] }).calcParams;
    const period   = Math.max(2, Math.floor(rawParams?.[0] ?? DEFAULT_RSI_PERIOD));
    const lookback = Math.max(1, Math.floor(rawParams?.[1] ?? DEFAULT_PIVOT_LOOKBACK));
    const rsi = computeRsi(bars, period);

    const results: RsiDivergenceResult[] = bars.map((_, i) => ({
      rsi: rsi[i],
      divType: null,
      pairFromIndex: null,
    }));

    let lastPivotLowIdx = -1;
    let lastPivotHighIdx = -1;

    for (let i = lookback; i < n - lookback; i++) {
      if (rsi[i] === null) continue;

      if (isPivotLow(bars, i, lookback)) {
        if (
          lastPivotLowIdx !== -1 &&
          rsi[lastPivotLowIdx] !== null &&
          bars[i].low < bars[lastPivotLowIdx].low &&
          (rsi[i] as number) > (rsi[lastPivotLowIdx] as number)
        ) {
          results[i].divType = 'bullish';
          results[i].pairFromIndex = lastPivotLowIdx;
        }
        lastPivotLowIdx = i;
      }

      if (isPivotHigh(bars, i, lookback)) {
        if (
          lastPivotHighIdx !== -1 &&
          rsi[lastPivotHighIdx] !== null &&
          bars[i].high > bars[lastPivotHighIdx].high &&
          (rsi[i] as number) < (rsi[lastPivotHighIdx] as number)
        ) {
          results[i].divType = 'bearish';
          results[i].pairFromIndex = lastPivotHighIdx;
        }
        lastPivotHighIdx = i;
      }
    }

    return results;
  },

  draw: (params: unknown): boolean => {
    if (!useChartStore.getState().rsiDivergenceEnabled) return true;

    const { ctx, visibleRange, xAxis, yAxis, indicator } = params as DrawParams;
    const results = indicator.result;
    if (!results.length) return true;

    const { from, to } = visibleRange;
    const scanFrom = Math.max(0, from - DRAW_LOOKBACK_PAD);
    const scanTo = Math.min(results.length - 1, to);
    if (scanTo < scanFrom) return true;

    ctx.save();

    for (let i = scanFrom; i <= scanTo; i++) {
      const row = results[i];
      if (!row.divType || row.pairFromIndex === null || row.rsi === null) continue;

      const pairRow = results[row.pairFromIndex];
      if (!pairRow || pairRow.rsi === null) continue;

      const color = row.divType === 'bullish' ? BULLISH_COLOR : BEARISH_COLOR;

      const x1 = xAxis.convertToPixel(row.pairFromIndex);
      const y1 = yAxis.convertToPixel(pairRow.rsi);
      const x2 = xAxis.convertToPixel(i);
      const y2 = yAxis.convertToPixel(row.rsi);

      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x1, y1, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x2, y2, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
    return true; // suppress default figure rendering
  },
};

export default rsiDivergence;
